/**
 * BUILD MY PROJECT — the Exchange control surface.
 *
 * One project, answered in the order a PM actually asks:
 *   What does this project still need?   → Project Assembly
 *   What does this package require?      → Demand profile (with provenance)
 *   Who can fulfil it?                   → Candidate providers
 *   Who is actually eligible?            → Eligibility gates, separately
 *   Who operationally fits?              → Fit score, separately
 *   Who do we already know?              → Relationship context, separately
 *   What moves this forward?             → RFQ handoff into the existing
 *                                          BidPackage workspace
 *
 * Nothing here computes domain truth. Package states come from real record
 * states, eligibility and fit and relationship all arrive decided from the
 * backend, and there is deliberately no coverage percentage anywhere —
 * OMNΛS does not express one, so neither does this screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "../services/api";
import exchangeApi from "../services/exchangeService";
import {
  executionIntelligenceApi,
  requirementsApi,
  awardWorkflowApi,
  rfqApi,
  decisionEpisodeApi,
} from "../services/intelligenceService";
import { classifyWorkflowError } from "../lib/workflowErrors";
import { createSequencer } from "../lib/sequencer";
import ExecutionBarrierMap from "../features/runtime/ExecutionBarrierMap";
import {
  presentBarrierMap,
  findPackage as findBarrierPackage,
} from "../features/runtime/barrierPresentation";
import DecisionTwinPanel from "../features/runtime/DecisionTwinPanel";
import DecisionMemoryPanel from "../features/runtime/DecisionMemoryPanel";
import { presentEpisodes } from "../features/runtime/episodePresentation";
import { presentScenario } from "../features/runtime/decisionTwinPresentation";
import { useAuth } from "../App";
import {
  CAPABILITY_ACCESS,
  anyCapabilityAccess,
  hasCapability,
} from "../lib/capabilities";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  SectionLoading,
  SectionEmpty,
  SectionError,
  FeatureDisabled,
  NotEntitled,
  NoAccess,
  ClassifiedSectionState,
} from "../components/workflow/WorkflowStates";
import { realismDisplay } from "../features/readiness/readinessPresentation";
import DemandProfilePanel from "../features/exchange/DemandProfilePanel";
import ProviderMatchCard from "../features/exchange/ProviderMatchCard";
import ShortlistPanel from "../features/exchange/ShortlistPanel";
import RfqHandoffPanel from "../features/exchange/RfqHandoffPanel";
import RfqBlockerDialog from "../features/exchange/RfqBlockerDialog";
import PackageStoryPanel from "../features/exchange/PackageStoryPanel";
import {
  presentAssemblyRow,
  sortAssemblyRows,
  summarizeAssembly,
  groupCandidates,
  emptyCandidateMeaning,
  summarizeShortlistProposal,
  SHORTLIST_DECISION_COPY,
  presentRfqError,
  payloadForSubject,
} from "../features/exchange/exchangePresentation";
import {
  Network,
  Package as PackageIcon,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  Search,
  Send,
  Building2,
  Users,
  ClipboardList,
  ListChecks,
  Radar,
  OctagonAlert,
  FlaskConical,
  History,
} from "lucide-react";

/** How many packages get their candidate counts pre-loaded. Disclosed in UI. */
const MATCH_PREVIEW_CAP = 6;


const TONE_CLASS = {
  good: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  active: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  attention: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

const AssemblyRow = ({ row, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(row.packageId)}
    aria-current={selected ? "true" : undefined}
    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
      selected
        ? "border-primary/50 bg-primary/10"
        : "border-border/60 bg-muted/20 hover:bg-muted/40"
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{row.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">
          {row.trade || "No trade recorded"}
          {row.dueDate ? ` · due ${row.dueDate}` : ""}
        </p>
      </div>
      <Badge
        variant="outline"
        className={`${TONE_CLASS[row.tone] || TONE_CLASS.unknown} font-medium text-[10px] shrink-0`}
      >
        {row.label}
      </Badge>
    </div>
    {row.detail && <p className="text-[11px] text-muted-foreground mt-1">{row.detail}</p>}
  </button>
);

const BuildMyProjectPage = () => {
  const navigate = useNavigate();
  const { user, capabilities, capabilitiesEstablished } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const projectId = searchParams.get("project_id") || "";
  const packageId = searchParams.get("package_id") || "";
  // In the URL so the RFQ workspace can deep-link straight to the shortlist
  // rather than dropping the PM on a tab they then have to find.
  const tab = searchParams.get("tab") || "providers";

  const [projects, setProjects] = useState(null);
  const [projectsError, setProjectsError] = useState(null);

  // Packages are stored WITH the project they were loaded for. A set that
  // belongs to a different project reads as "not loaded yet" rather than
  // briefly rendering the previous project's packages under the new name.
  const [packageSet, setPackageSet] = useState(null); // {projectId, rows, quoteCounts}
  const [packagesError, setPackagesError] = useState(null);

  // packageId → {counts} | {error: classified}. A key that is absent means
  // "never checked", which is a different fact from "checked, found nothing".
  const [matchInfo, setMatchInfo] = useState({});
  // packageId → the backend's own RFQ release facets for that package.
  const [releaseFacts, setReleaseFacts] = useState({});
  const [matchProbeError, setMatchProbeError] = useState(null);
  const [matchPreviewCapped, setMatchPreviewCapped] = useState(false);

  const [sourcing, setSourcing] = useState(null);
  const [sourcingError, setSourcingError] = useState(null);
  const [sourcingLoading, setSourcingLoading] = useState(false);

  const [realism, setRealism] = useState(null);

  const [shortlist, setShortlist] = useState(null);
  const [shortlistError, setShortlistError] = useState(null);
  const [proposing, setProposing] = useState(false);
  const [decidingEntry, setDecidingEntry] = useState(false);

  const [releases, setReleases] = useState(null);
  const [releasesError, setReleasesError] = useState(null);
  /**
   * Every shortlist entry with a preparation in flight, not just the last one
   * clicked. A single slot let one preparation finishing clear another row's
   * spinner, re-enabling a button whose request was still running.
   */
  const [preparingEntries, setPreparingEntries] = useState([]);
  const [authorizing, setAuthorizing] = useState(false);
  // The two blocking refusals (identity review, duplicate lineage) are
  // structured answers, not failures — they get a dialog, not a toast.
  const [rfqBlocker, setRfqBlocker] = useState(null);

  /**
   * The two facets the story needs that no other panel loads: the register
   * requirements naming this package, and the award proposals on it. Both are
   * read-only, both tolerate their own failure, and neither is required for
   * anything else on the screen to work.
   */
  const [story, setStory] = useState(null); // {packageId, requirements, awardProposals}

  /**
   * The subject a write should reload, read at reload time rather than
   * captured when the write started. A prepare or authorize fires from a
   * plain handler, so it holds whatever `loadReleases` existed at that render;
   * if the user switches package while the request is in flight, that stale
   * callback would fetch the previous package, take a fresh sequencer ticket,
   * and store the result under a tag the current render rejects — leaving the
   * panel on a spinner nothing clears.
   */
  // Scenarios are keyed by quoteId and tagged with the package they belong to,
  // so switching packages can never show one package's futures under another.
  const [twin, setTwin] = useState(null); // {packageId, quotes}
  const [twinLoading, setTwinLoading] = useState(false);
  const [twinError, setTwinError] = useState(null);
  const [scenarios, setScenarios] = useState({}); // {packageId, byQuote, errors}
  const [runningQuoteId, setRunningQuoteId] = useState(null);

  const [memory, setMemory] = useState(null); // {projectId, presented, contractVersion}
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memoryError, setMemoryError] = useState(null);

  const [barrier, setBarrier] = useState(null); // {projectId, presented, contractVersion}
  const [barrierLoading, setBarrierLoading] = useState(false);
  const [barrierError, setBarrierError] = useState(null);

  const subjectRef = useRef({ projectId: '', packageId: '' });
  subjectRef.current = { projectId, packageId };

  const packagesSeq = useRef(createSequencer()).current;
  const sourcingSeq = useRef(createSequencer()).current;
  const realismSeq = useRef(createSequencer()).current;
  const shortlistSeq = useRef(createSequencer()).current;
  const releasesSeq = useRef(createSequencer()).current;
  const storySeq = useRef(createSequencer()).current;
  const barrierSeq = useRef(createSequencer()).current;
  const quotesSeq = useRef(createSequencer()).current;
  const memorySeq = useRef(createSequencer()).current;

  /**
   * Two writers fill `releaseFacts`: the bounded probe that populates the
   * assembly list, and the full read that runs when a package is opened or
   * written to. The probe is both the slower and the less informed of the
   * two, so a probe response that was issued before an authoritative write
   * must not land on top of it — a prepared release would vanish from the row
   * seconds after the PM prepared it.
   *
   * Every write stamps its package with a rising number. A writer only
   * commits if its own stamp is still the package's current one.
   */
  const releaseFactStamps = useRef({});
  const releaseStampCounter = useRef(0);
  const claimReleaseFacts = useCallback((pkgId) => {
    releaseStampCounter.current += 1;
    releaseFactStamps.current[pkgId] = releaseStampCounter.current;
    return releaseStampCounter.current;
  }, []);
  const releaseFactsStillMine = useCallback(
    (pkgId, stamp) => releaseFactStamps.current[pkgId] === stamp,
    []
  );

  // Four capabilities, served by the session. The six role arrays that used
  // to stand here are gone: they mirrored backend lists no test could
  // compare them against, and the ROLE_ALIASES expansion that kept them
  // honest was invisible from this file.
  //
  // Still NOT nested, and the backend says so rather than a comment: an
  // estimator holds exchange.read and exchange.propose but not
  // exchange.decide, and holds rfq.publish without rfq.authorize — which the
  // route enforces as an intersection and the session projects as one.
  const exchangeCapabilityAccess = anyCapabilityAccess(
    capabilitiesEstablished,
    capabilities,
    "rfq.create",
    "exchange.read"
  );
  const canSeeExchange = exchangeCapabilityAccess === CAPABILITY_ACCESS.ALLOWED;
  const canPropose = hasCapability(capabilities, "exchange.propose");
  const canDecide = hasCapability(capabilities, "exchange.decide");
  const canPrepareRfq = hasCapability(capabilities, "rfq.create");
  const canAuthorizeRelease = hasCapability(capabilities, "rfq.authorize");

  // ---- projects -----------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/projects")
      .then(({ data }) => {
        if (cancelled) return;
        const rows = data || [];
        setProjects(rows);
        if (!projectId && rows.length > 0) {
          setSearchParams({ project_id: rows[0].id }, { replace: true });
        }
      })
      .catch((err) => !cancelled && setProjectsError(classifyWorkflowError(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- packages for the selected project ---------------------------------
  const loadPackages = useCallback(async () => {
    if (!projectId) return;
    const ticket = packagesSeq.next();
    setPackageSet(null);
    setPackagesError(null);
    /**
     * `matchInfo` and `releaseFacts` are deliberately NOT cleared here.
     *
     * Refresh re-reads the package list, and the bounded probe that follows
     * refills both maps — but only for the first MATCH_PREVIEW_CAP packages.
     * The open package's entries came from its own full read, which Refresh
     * does not repeat, so clearing the maps dropped facts nothing would put
     * back and reverted a row a PM had just acted on to "not checked".
     *
     * Both maps are keyed by package id, so a stale entry can only be
     * overwritten by a newer read of the same package. Switching project
     * clears them, below.
     */
    setMatchProbeError(null);
    setMatchPreviewCapped(false);
    try {
      // The RFQ list carries durable quote counts — the fact that turns
      // "sourced" into "has responses". It is separately entitled, so fall
      // back to the legacy package list and say what is missing.
      const { data } = await apiClient.get("/rfq/packages", { params: { project_id: projectId } });
      if (!packagesSeq.isCurrent(ticket)) return;
      setPackageSet({ projectId, rows: data || [], quoteCounts: true });
    } catch (err) {
      const classified = classifyWorkflowError(err);
      if (["disabled", "not-entitled", "no-access"].includes(classified.kind)) {
        try {
          const { data } = await apiClient.get("/bids", { params: { project_id: projectId } });
          if (!packagesSeq.isCurrent(ticket)) return;
          setPackageSet({
            projectId,
            quoteCounts: false,
            rows: (data || [])
              .filter((row) => row.project_id === projectId)
              .map((row) => ({
                bidPackageId: row.id,
                title: row.title,
                trade: row.trade,
                status: row.status,
                dueDate: row.due_date,
                estimatedValue: row.estimated_value,
              })),
          });
          return;
        } catch (fallbackErr) {
          if (!packagesSeq.isCurrent(ticket)) return;
          setPackagesError(classifyWorkflowError(fallbackErr));
          return;
        }
      }
      if (!packagesSeq.isCurrent(ticket)) return;
      setPackagesError(classified);
    }
  }, [projectId, packagesSeq]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  // Probe results belong to one project. Changing project retires them;
  // refreshing within a project does not.
  useEffect(() => {
    setMatchInfo({});
    setReleaseFacts({});
    releaseFactStamps.current = {};
  }, [projectId]);

  const packages = packageSet?.projectId === projectId ? packageSet.rows : null;
  const quoteCountsAvailable = packageSet?.projectId === projectId ? packageSet.quoteCounts : true;

  // ---- project realism (execution impact) --------------------------------
  useEffect(() => {
    if (!projectId) return;
    const ticket = realismSeq.next();
    setRealism(null);
    executionIntelligenceApi
      .realism(projectId)
      .then((data) => realismSeq.isCurrent(ticket) && setRealism({ data, error: null }))
      .catch(
        (err) =>
          realismSeq.isCurrent(ticket) && setRealism({ data: null, error: classifyWorkflowError(err) })
      );
  }, [projectId, realismSeq]);

  // ---- candidate counts for the first N sourcing-relevant packages -------
  useEffect(() => {
    if (!projectId || !packages) return;
    let cancelled = false;
    const needsSourcing = packages.filter((pkg) => {
      const status = String(pkg.status || "").toLowerCase();
      const quotes = pkg.currentQuoteCount;
      return (
        status !== "awarded" &&
        status !== "cancelled" &&
        status !== "missed" &&
        !(typeof quotes === "number" && quotes > 0)
      );
    });
    setMatchPreviewCapped(needsSourcing.length > MATCH_PREVIEW_CAP);

    /**
     * The commercial half of the list, from the backend's own release facets.
     *
     * This is bounded by the SAME cap but over the package list itself, not
     * over the sourcing-relevant subset: a package that already has quotes is
     * excluded from sourcing yet is exactly where a released provider matters.
     * Scanning a different set for each half made a row's badge depend on
     * which half happened to cover it, and opening a package then changed it.
     */
    const runReleases = async () => {
      for (const pkg of packages.slice(0, MATCH_PREVIEW_CAP)) {
        if (cancelled) return;
        const stamp = claimReleaseFacts(pkg.bidPackageId);
        try {
          const rfq = await exchangeApi.listRfqReleases(projectId, pkg.bidPackageId);
          if (cancelled) return;
          // A full read of this package overtook the probe: it knows more, so
          // it keeps the row.
          if (!releaseFactsStillMine(pkg.bidPackageId, stamp)) continue;
          setReleaseFacts((prev) => ({
            ...prev,
            [pkg.bidPackageId]: {
              stateFacets: rfq.stateFacets || {},
              downstreamFacets: rfq.downstreamFacets || {},
              count: rfq.count || 0,
            },
          }));
        } catch {
          // The RFQ surface carries its own entitlement and flag. Its absence
          // leaves the sourcing state exactly as it was.
          return;
        }
      }
    };

    const run = async () => {
      for (const pkg of needsSourcing.slice(0, MATCH_PREVIEW_CAP)) {
        if (cancelled) return;
        try {
          const data = await exchangeApi.matches(projectId, pkg.bidPackageId);
          if (cancelled) return;
          setMatchInfo((prev) => ({ ...prev, [pkg.bidPackageId]: { counts: data.counts || null } }));
        } catch (err) {
          if (cancelled) return;
          const classified = classifyWorkflowError(err);
          // Availability refusals are properties of the surface: the next
          // package would answer the same way, so stop and say so once.
          if (["disabled", "not-entitled", "no-access"].includes(classified.kind)) {
            setMatchProbeError(classified);
            return;
          }
          // Anything else (a deleted package, a server fault on one row) is
          // that package's problem. Record it and keep checking the others.
          setMatchInfo((prev) => ({ ...prev, [pkg.bidPackageId]: { error: classified } }));
        }
      }
    };
    run();
    runReleases();
    return () => {
      cancelled = true;
    };
  }, [projectId, packages, claimReleaseFacts, releaseFactsStillMine]);

  // ---- sourcing detail for the selected package --------------------------
  // ---- what was known when the decision was made --------------------------
  /**
   * `decision-episode-1`, filtered to award decisions because this page is the
   * commercial context. The contract offers no package filter, so the panel
   * says the list is project-wide rather than implying otherwise.
   */
  const loadMemory = useCallback(async () => {
    if (!projectId) {
      memorySeq.invalidate();
      setMemory(null);
      setMemoryError(null);
      setMemoryLoading(false);
      return;
    }
    const ticket = memorySeq.next();
    setMemoryLoading(true);
    setMemory(null);
    setMemoryError(null);
    try {
      const data = await decisionEpisodeApi.episodes(projectId, { surface: "AWARD_PROPOSAL" });
      if (!memorySeq.isCurrent(ticket)) return;
      setMemory({
        projectId,
        presented: presentEpisodes(data),
        contractVersion: data?.contractVersion || null,
      });
    } catch (err) {
      if (!memorySeq.isCurrent(ticket)) return;
      setMemoryError(err);
    } finally {
      if (memorySeq.isCurrent(ticket)) setMemoryLoading(false);
    }
  }, [projectId, memorySeq]);

  useEffect(() => {
    loadMemory();
  }, [loadMemory]);

  // ---- the futures this decision would produce ----------------------------
  /**
   * Quotes for the selected package. The Decision Twin needs a live quote id
   * to simulate against; the simulator refuses a dead one exactly as the
   * award path would.
   */
  const loadQuotes = useCallback(async () => {
    if (!packageId) {
      quotesSeq.invalidate();
      setTwin(null);
      setTwinError(null);
      setTwinLoading(false);
      // Futures belong to the package that produced them. Dropping them on a
      // package change is the point, not a cleanup detail.
      setScenarios({});
      return;
    }
    const ticket = quotesSeq.next();
    setTwinLoading(true);
    setTwin(null);
    setTwinError(null);
    setScenarios({});
    try {
      const data = await rfqApi.listQuotes(packageId);
      if (!quotesSeq.isCurrent(ticket)) return;
      setTwin({ packageId, quotes: Array.isArray(data) ? data : [] });
    } catch (err) {
      if (!quotesSeq.isCurrent(ticket)) return;
      setTwinError(err);
    } finally {
      if (quotesSeq.isCurrent(ticket)) setTwinLoading(false);
    }
  }, [packageId, quotesSeq]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  /**
   * Run one future. One scenario per question — the contract allows at most
   * one PROVIDER_SELECTED, and batching several providers into one request
   * would be asking a question the simulator cannot answer.
   *
   * The result is stored against the package it was run for, so a late
   * response after a package switch is discarded rather than rendered.
   */
  const runScenario = useCallback(
    async (quote) => {
      const forPackage = packageId;
      const forProject = projectId;
      if (!forProject || !quote?.quoteId) return;
      setRunningQuoteId(quote.quoteId);
      try {
        const data = await executionIntelligenceApi.counterfactual(forProject, [
          {
            kind: "PROVIDER_SELECTED",
            bidPackageId: quote.bidPackageId || forPackage,
            quoteId: quote.quoteId,
          },
        ]);
        if (subjectRef.current.packageId !== forPackage) return;
        setScenarios((prev) => ({
          ...prev,
          packageId: forPackage,
          byQuote: { ...(prev.byQuote || {}), [quote.quoteId]: presentScenario(data) },
        }));
      } catch (err) {
        if (subjectRef.current.packageId !== forPackage) return;
        setScenarios((prev) => ({
          ...prev,
          packageId: forPackage,
          errors: { ...(prev.errors || {}), [quote.quoteId]: err },
        }));
      } finally {
        if (subjectRef.current.packageId === forPackage) setRunningQuoteId(null);
      }
    },
    [projectId, packageId]
  );

  // ---- what stands in the way of this package executing -------------------
  /**
   * `executable-work-package-1`. Loaded per PROJECT (the endpoint answers for
   * every package at once) and then narrowed to the selected package, so
   * switching packages does not re-fetch.
   *
   * Tagged with its projectId for the same reason every other stream here is:
   * a response that arrives after the user has moved to another project must
   * never render under the new project's name.
   */
  const loadBarrier = useCallback(async () => {
    if (!projectId) {
      barrierSeq.invalidate();
      setBarrier(null);
      setBarrierError(null);
      setBarrierLoading(false);
      return;
    }
    const ticket = barrierSeq.next();
    setBarrierLoading(true);
    setBarrier(null);
    setBarrierError(null);
    try {
      const data = await executionIntelligenceApi.workPackages(projectId);
      if (!barrierSeq.isCurrent(ticket)) return;
      setBarrier({ projectId, presented: presentBarrierMap(data), contractVersion: data?.contractVersion || null });
    } catch (err) {
      if (!barrierSeq.isCurrent(ticket)) return;
      // The raw error is kept: the barrier panel distinguishes the flag, the
      // entitlement, the capability and the role, and a pre-classified
      // string would collapse four different remedies into one.
      setBarrierError(err);
    } finally {
      if (barrierSeq.isCurrent(ticket)) setBarrierLoading(false);
    }
  }, [projectId, barrierSeq]);

  useEffect(() => {
    loadBarrier();
  }, [loadBarrier]);

  const loadSourcing = useCallback(async () => {
    if (!projectId || !packageId) {
      // Deselecting is itself a subject change: drop anything in flight so a
      // late response cannot repopulate a panel the user just closed.
      sourcingSeq.invalidate();
      setSourcing(null);
      setSourcingError(null);
      setSourcingLoading(false);
      return;
    }
    const ticket = sourcingSeq.next();
    setSourcingLoading(true);
    setSourcing(null);
    setSourcingError(null);
    try {
      // The matches payload inlines the full demand profile, so one request
      // answers both "what does this need" and "who can fulfil it".
      const data = await exchangeApi.matches(projectId, packageId);
      if (!sourcingSeq.isCurrent(ticket)) return;
      // Tagged with the package it describes, for the same reason packages
      // are tagged with their project: the heading and the RFQ button beside
      // this panel come from the CURRENT packageId, so an untagged payload
      // would render one package's providers under another package's name.
      setSourcing({ packageId, data });
      // Opening a package IS a probe of it — feed the result back so the
      // assembly row stops saying the sourcing state is unknown.
      setMatchInfo((prev) => ({ ...prev, [packageId]: { counts: data.counts || null } }));
    } catch (err) {
      const classified = classifyWorkflowError(err);
      if (!sourcingSeq.isCurrent(ticket)) return;
      // A failed probe is still a probe. Without this the assembly row would
      // keep saying "not checked yet" about a package we just failed to check.
      setMatchInfo((prev) => ({ ...prev, [packageId]: { error: classified } }));
      // Matching may fail for reasons the profile does not share; still show
      // the PM what the package requires.
      if (!["disabled", "not-entitled", "no-access", "not-found"].includes(classified.kind)) {
        try {
          const profile = await exchangeApi.demandProfile(projectId, packageId);
          if (!sourcingSeq.isCurrent(ticket)) return;
          setSourcing({
            packageId,
            data: { demandProfile: profile, candidates: null, matchingFailed: classified },
          });
          return;
        } catch {
          /* fall through to the error state */
        }
      }
      if (sourcingSeq.isCurrent(ticket)) setSourcingError(classified);
    } finally {
      if (sourcingSeq.isCurrent(ticket)) setSourcingLoading(false);
    }
  }, [projectId, packageId, sourcingSeq]);

  useEffect(() => {
    loadSourcing();
  }, [loadSourcing]);

  // ---- the human shortlist for the selected package -----------------------
  /**
   * `quiet` refreshes in place after a write: it keeps the current payload on
   * screen instead of blanking it, so a decision dialog holding a reason the
   * user typed is not unmounted out from under them by a reload.
   *
   * The payload is tagged with the package it describes, exactly as packages
   * and sourcing are — a write's reload closure captures the package that was
   * current when the write started, and without the tag a slow reload could
   * land one package's shortlist under another package's name.
   */
  const loadShortlist = useCallback(
    async ({ quiet = false } = {}) => {
      const { projectId: pid, packageId: pkgId } = subjectRef.current;
      if (!pid || !pkgId) {
        shortlistSeq.invalidate();
        setShortlist(null);
        setShortlistError(null);
        return;
      }
      const ticket = shortlistSeq.next();
      if (!quiet) setShortlist(null);
      setShortlistError(null);
      try {
        const data = await exchangeApi.getShortlist(pid, pkgId);
        if (shortlistSeq.isCurrent(ticket)) setShortlist({ packageId: pkgId, data });
      } catch (err) {
        if (shortlistSeq.isCurrent(ticket)) {
          setShortlist(null);
          setShortlistError(classifyWorkflowError(err));
        }
      }
    },
    /**
     * projectId/packageId stay in the dependency list even though the body
     * reads them from the ref: they are what makes this callback — and the
     * effect that runs it — fire when the user switches subject. The ref only
     * decides which subject a WRITE's reload targets.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, packageId, shortlistSeq]
  );

  useEffect(() => {
    loadShortlist();
  }, [loadShortlist]);

  /**
   * Proposing is a bulk re-run of matching, not a per-candidate action — the
   * backend takes no candidate id and has no path to any state but PROPOSED.
   * It is additive: an entry a human already decided is counted, never
   * revived, so re-running can never undo an exclusion.
   */
  const proposeShortlist = async () => {
    if (!projectId || !packageId) return;
    setProposing(true);
    try {
      const result = await exchangeApi.proposeShortlist(projectId, packageId);
      const { created, lines } = summarizeShortlistProposal(result);
      toast.success(
        created === 0
          ? "No new candidates to propose."
          : `${created} candidate${created === 1 ? "" : "s"} proposed for your decision.`,
        lines.length > 0 ? { description: lines.join(" ") } : undefined
      );
      await loadShortlist({ quiet: true });
    } catch (err) {
      const classified = classifyWorkflowError(err);
      if (classified.code === "shortlist_proposal_conflict") {
        // The whole run was rolled back — re-running is the documented fix.
        toast.error("Another proposal run got there first. Nothing was recorded — try again.");
        await loadShortlist({ quiet: true });
      } else {
        toast.error(classified.message);
      }
    } finally {
      setProposing(false);
    }
  };

  /**
   * One membership decision. Returns true when it stuck, so the dialog only
   * closes on success and a rejected decision keeps the reason the human
   * already typed.
   */
  const decideShortlistEntry = async (row, decision, note) => {
    setDecidingEntry(true);
    try {
      await exchangeApi.decideShortlistEntry(row.entryId, decision, note);
      toast.success(
        `Recorded — ${row.title || row.recordLabel} ${
          SHORTLIST_DECISION_COPY[decision]?.past || decision.toLowerCase()
        }.`
      );
      await loadShortlist({ quiet: true });
      return true;
    } catch (err) {
      const classified = classifyWorkflowError(err);
      // A stale view is the common cause: someone else moved this entry.
      if (classified.code === "invalid_state_transition") {
        toast.error(`${classified.message} Reloading the current state.`);
        await loadShortlist({ quiet: true });
      } else if (classified.kind === "not-found") {
        toast.error("That shortlist entry no longer exists.");
        await loadShortlist({ quiet: true });
      } else {
        toast.error(classified.message);
      }
      return false;
    } finally {
      setDecidingEntry(false);
    }
  };

  // ---- RFQ releases for the selected package -----------------------------
  const loadReleases = useCallback(
    async ({ quiet = false } = {}) => {
      // Read the CURRENT subject, so a reload fired by a write always targets
      // the package the user is actually looking at.
      const { projectId: pid, packageId: pkgId } = subjectRef.current;
      if (!pid || !pkgId) {
        releasesSeq.invalidate();
        setReleases(null);
        setReleasesError(null);
        return;
      }
      const ticket = releasesSeq.next();
      if (!quiet) setReleases(null);
      setReleasesError(null);
      try {
        const data = await exchangeApi.listRfqReleases(pid, pkgId);
        if (!releasesSeq.isCurrent(ticket)) return;
        setReleases({ packageId: pkgId, data });
        // Opening a package reads its releases in full — feed the facets back
        // so the assembly row agrees with the panel beside it. This is the
        // authoritative write; claiming the stamp retires any probe still in
        // flight for the same package.
        claimReleaseFacts(pkgId);
        setReleaseFacts((prev) => ({
          ...prev,
          [pkgId]: {
            stateFacets: data.stateFacets || {},
            downstreamFacets: data.downstreamFacets || {},
            count: data.count || 0,
          },
        }));
      } catch (err) {
        if (releasesSeq.isCurrent(ticket)) {
          setReleases(null);
          setReleasesError(classifyWorkflowError(err));
        }
      }
    },
    /**
     * projectId/packageId stay in the dependency list even though the body
     * reads them from the ref: they are what makes this callback — and the
     * effect that runs it — fire when the user switches subject. The ref only
     * decides which subject a WRITE's reload targets.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, packageId, releasesSeq]
  );

  useEffect(() => {
    loadReleases();
  }, [loadReleases]);

  /**
   * Prepare one shortlisted provider for RFQ.
   *
   * Two of the refusals here are structured answers rather than failures —
   * identity review and duplicate lineage — and both are blocking by design.
   * They open a dialog that explains the situation and routes to identity
   * resolution; neither offers a bypass, because both exist to stop one firm
   * entering a package twice or a quote starting under the wrong company.
   *
   * A replayed preparation (`created: false`) is not a duplicate: it is the
   * documented idempotent answer to a retry.
   */
  const prepareRfq = async (row) => {
    if (!projectId || !packageId || !row?.entryId) return;
    setPreparingEntries((prev) => (prev.includes(row.entryId) ? prev : [...prev, row.entryId]));
    try {
      const result = await exchangeApi.prepareRfq(projectId, packageId, row.entryId);
      toast.success(
        result.created === false
          ? "Already prepared — showing the existing handoff record."
          : "Prepared for RFQ. Nothing has been sent; a release still needs authorizing.",
        // Named gaps the backend could not establish, in its own words.
        (result.missingInformation || []).length > 0
          ? { description: result.missingInformation.join(" ") }
          : undefined
      );
      await loadReleases({ quiet: true });
    } catch (err) {
      const classified = classifyWorkflowError(err);
      const presented = presentRfqError(classified);
      if (presented && ["identity-review", "duplicate-lineage"].includes(presented.kind)) {
        setRfqBlocker(presented);
      } else if (presented) {
        toast.error(presented.title, { description: presented.explanation });
        // All three mean this view is behind the record: re-read both, or the
        // stale row keeps offering an action the backend will refuse again.
        if (["raced", "stale-view", "not-eligible"].includes(presented.kind)) {
          await loadReleases({ quiet: true });
          await loadShortlist({ quiet: true });
        }
      } else {
        toast.error(classified.message);
      }
    } finally {
      setPreparingEntries((prev) => prev.filter((id) => id !== row.entryId));
    }
  };

  /**
   * Record a human's release authorization.
   *
   * `confirmed` arrives from the dialog's deliberate act and is never
   * defaulted here. Returns true only when it stuck, so a refusal keeps the
   * note the user typed rather than discarding it with the dialog.
   */
  const authorizeRelease = async (row, { confirmed, note }) => {
    setAuthorizing(true);
    try {
      const result = await exchangeApi.authorizeRfqRelease(row.releaseId, { confirmed, note });
      toast.success(
        result.changed === false
          ? "Already authorized — no second authorization was recorded."
          : "Released for RFQ. Internal authorization recorded; nothing was sent externally."
      );
      await loadReleases({ quiet: true });
      return true;
    } catch (err) {
      const classified = classifyWorkflowError(err);
      const presented = presentRfqError(classified);
      if (presented?.kind === "not-eligible") {
        // The human decision moved under this release since it was prepared.
        toast.error(presented.title, { description: presented.explanation });
        await loadReleases({ quiet: true });
        await loadShortlist({ quiet: true });
      } else if (presented) {
        toast.error(presented.title, { description: presented.explanation });
        if (presented.kind === "stale-view") await loadReleases({ quiet: true });
      } else if (classified.kind === "confirmation") {
        toast.error("That needs an explicit confirmation before it can be recorded.");
      } else {
        toast.error(classified.message);
      }
      return false;
    } finally {
      setAuthorizing(false);
    }
  };

  // ---- the two story facets nothing else on this screen loads ------------
  const loadStory = useCallback(async () => {
    const { projectId: pid, packageId: pkgId } = subjectRef.current;
    if (!pid || !pkgId) {
      storySeq.invalidate();
      setStory(null);
      return;
    }
    const ticket = storySeq.next();
    setStory(null);
    // Each half is optional and separately entitled. A failure leaves that
    // facet reading "not read" rather than blanking the story or the page.
    const [reqs, proposals] = await Promise.all([
      requirementsApi
        .listForProject(pid)
        .then((data) => ({
          // The register bounds its own read. A count taken over a bounded
          // read is a floor, and the story says so rather than presenting it
          // as this package's total obligation.
          rows: (data.requirements || []).filter((r) => r.bidPackageId === pkgId),
          truncated: data.truncated === true,
        }))
        .catch(() => null),
      awardWorkflowApi.listProposals(pkgId).catch(() => null),
    ]);
    if (!storySeq.isCurrent(ticket)) return;
    setStory({
      packageId: pkgId,
      requirements: reqs?.rows || null,
      requirementsTruncated: reqs?.truncated === true,
      awardProposals: proposals,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, packageId, storySeq]);

  useEffect(() => {
    loadStory();
  }, [loadStory]);

  // ---- derived view models ------------------------------------------------
  const rows = useMemo(() => {
    if (!packages) return [];
    return sortAssemblyRows(
      packages.map((pkg) => {
        const info = matchInfo[pkg.bidPackageId];
        return presentAssemblyRow(pkg, info?.counts ?? null, {
          matchesUnavailable: Boolean(matchProbeError) || Boolean(info?.error),
          sourcingProbed: Boolean(info),
          releaseFacts: releaseFacts[pkg.bidPackageId] || null,
        });
      })
    );
  }, [packages, matchInfo, matchProbeError, releaseFacts]);

  const summary = useMemo(() => summarizeAssembly(rows), [rows]);
  const selectedRow = rows.find((row) => row.packageId === packageId) || null;
  // A payload for a different package reads as "not loaded", never as this
  // package's answer.
  const sourcingData = sourcing?.packageId === packageId ? sourcing.data : null;
  const shortlistData = shortlist?.packageId === packageId ? shortlist.data : null;
  const grouped = useMemo(
    () => (sourcingData?.candidates ? groupCandidates(sourcingData.candidates) : null),
    [sourcingData]
  );
  const emptyMeaning = sourcingData?.candidates ? emptyCandidateMeaning(sourcingData) : null;

  /**
   * A shortlist entry stores no provider name — the only reliable way back to
   * one is the listing id its match evidence recorded. This index is that
   * join, and it is intentionally built from the CURRENT match set, so an
   * entry whose listing has since left the set is shown as unresolved rather
   * than given a name from somewhere else.
   */
  const candidatesByListing = useMemo(() => {
    const index = {};
    (sourcingData?.candidates || []).forEach((candidate) => {
      if (candidate?.listingId) index[candidate.listingId] = candidate;
    });
    return index;
  }, [sourcingData]);

  /**
   * Candidates keyed by every network organization confirmed for them, which
   * is the only identifier a release and a match candidate share.
   */
  const candidatesByOrganization = useMemo(() => {
    const index = {};
    (sourcingData?.candidates || []).forEach((candidate) => {
      (candidate?.relationshipContext?.identity?.confirmedOrganizationIds || []).forEach((id) => {
        if (id && !index[id]) index[id] = candidate;
      });
    });
    return index;
  }, [sourcingData]);

  /** Listing ids already on the shortlist, so a match card can say so. */
  const shortlistedListingIds = useMemo(() => {
    const map = {};
    (shortlistData?.entries || []).forEach((entry) => {
      (entry?.matchBasis || []).forEach((basis) => {
        if (basis?.listingId) map[basis.listingId] = entry.state;
      });
    });
    return map;
  }, [shortlistData]);

  // stateFacets is computed over the entries the backend actually returned,
  // so this is a floor when the read was truncated — the panel says so.
  const shortlistAwaiting = shortlistData?.stateFacets?.PROPOSED || 0;

  const releasesData = payloadForSubject(releases, packageId);
  const storyData = story?.packageId === packageId ? story : null;

  /**
   * Which shortlist entries already have a handoff record, so the Prepare
   * control is not offered a second time for one already prepared. The
   * backend would answer idempotently anyway; this is so the screen reads
   * correctly, not so the write is safe.
   */
  const releaseStateByEntry = useMemo(() => {
    const map = {};
    (releasesData?.releases || []).forEach((release) => {
      if (release?.shortlistEntryId) map[release.shortlistEntryId] = release.state;
    });
    return map;
  }, [releasesData]);

  const releasesAwaitingAuthorization = releasesData?.stateFacets?.PREPARED || 0;

  const selectPackage = (id) => {
    setSearchParams(
      // Selecting a different package drops the tab: it belonged to the
      // package the user just left.
      id ? { project_id: projectId, package_id: id } : { project_id: projectId },
      { replace: false }
    );
  };

  /**
   * The workspaces that OWN each facet. Build My Project orchestrates into
   * them; it never reimplements what they do. The project tabs are
   * URL-addressable so a link can land on the facet it names rather than
   * dropping the PM on an overview to hunt for it.
   */
  const openWorkspace = (which) => {
    if (which === "requirements") navigate(`/projects/${projectId}?tab=requirements`);
    else if (which === "readiness") navigate(`/projects/${projectId}?tab=readiness`);
    else if (which === "package" && packageId) navigate(`/procurement/packages/${packageId}`);
    else if (which === "procurement") navigate("/procurement");
    else if (which === "control-tower") navigate(`/control-tower?project_id=${projectId}`);
    else navigate(`/projects/${projectId}`);
  };

  const selectTab = (next) => {
    setSearchParams(
      { project_id: projectId, ...(packageId ? { package_id: packageId } : {}), tab: next },
      { replace: true }
    );
  };

  if (exchangeCapabilityAccess === CAPABILITY_ACCESS.NOT_ESTABLISHED) {
    return (
      <div className="min-h-[16rem] flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Checking session authority…</p>
      </div>
    );
  }

  if (!canSeeExchange) {
    return (
      <NoAccess message="The Exchange is an internal sourcing surface. Your role does not include it." />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Network className="w-7 h-7 text-primary" />
            Build My Project
          </h1>
          <p className="text-muted-foreground mt-1">
            What this project still needs, who can fulfil it, and who qualifies.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={projectId}
            onValueChange={(value) => setSearchParams({ project_id: value })}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {(projects || []).map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* The provider directory is where identity matches are proposed
              and confirmed — and until at least one is confirmed, no match
              here can open a passport. Without this link that surface is
              only reachable from a passport you cannot yet open. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/exchange/providers")}
            title="The provider network: identity anchors, passports, and identity matches awaiting a decision"
          >
            <Users className="w-4 h-4 mr-1.5" />
            Provider network
          </Button>
          {projectId && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openWorkspace("procurement")}
                title="The procurement register — every package across every project"
              >
                <PackageIcon className="w-4 h-4 mr-1.5" />
                Procurement
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openWorkspace("control-tower")}
                title="Execution Control for this project"
              >
                <Radar className="w-4 h-4 mr-1.5" />
                Execution Control
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/projects/${projectId}`)}
                title="Open the project workspace"
              >
                <Building2 className="w-4 h-4 mr-1.5" />
                Project
              </Button>
            </>
          )}
        </div>
      </div>

      {projectsError && (
        <SectionError error={projectsError} label="Projects failed to load" />
      )}
      {projects && projects.length === 0 && (
        <SectionEmpty
          icon={Building2}
          title="No projects yet"
          hint="Create a project first; the Exchange assembles sourcing around a project's packages."
        />
      )}

      {projectId && (
        <>
          {/* Assembly summary. Every tile is a count of real package states —
              there is no coverage percentage, because OMNΛS states none. */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Card className="border-border">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground">Packages</p>
                <p className="text-2xl font-bold">{summary.total}</p>
              </CardContent>
            </Card>
            <Card className={summary.needsAttention ? "border-amber-500/40" : "border-border"}>
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground">Need attention</p>
                <p
                  className={`text-2xl font-bold ${summary.needsAttention ? "text-amber-400" : ""}`}
                >
                  {summary.needsAttention}
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground">Candidates found</p>
                <p className="text-2xl font-bold">{summary.readyToSource}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground">Not checked</p>
                <p className="text-2xl font-bold text-muted-foreground">{summary.unknown}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground">In commercial</p>
                <p className="text-2xl font-bold">{summary.inCommercial}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground">Assigned</p>
                <p className="text-2xl font-bold text-emerald-400">{summary.assigned}</p>
              </CardContent>
            </Card>
          </div>

          {/* Execution impact — the same realism verdict Execution Control shows */}
          {realism?.error && (
            <p className="text-[11px] text-muted-foreground">
              Execution impact unavailable —{" "}
              {realism.error.kind === "not-entitled"
                ? "project realism is not part of your plan."
                : realism.error.kind === "disabled"
                  ? "project realism is not enabled here."
                  : realism.error.kind === "no-access"
                    ? "your role does not include project realism."
                    : "it did not load. Sourcing below is unaffected."}
            </p>
          )}
          {realism?.data && (
            <Card className="border-border">
              <CardContent className="py-3 px-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Execution impact:</span>
                  <Badge
                    variant="outline"
                    className={`${realismDisplay(realism.data.classification).className} font-medium`}
                  >
                    {realismDisplay(realism.data.classification).label}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    Can finish on time: {realism.data.canFinishOnTime || "UNKNOWN"}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => openWorkspace("readiness")}
                >
                  What can start?
                  <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,380px)_1fr] gap-5 items-start">
            {/* PROJECT ASSEMBLY */}
            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-primary" />
                  Project assembly
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={loadPackages}
                  aria-label="Reload this project's packages"
                  title="Reload this project's packages"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </div>

              {packagesError ? (
                packagesError.kind === "not-entitled" ? (
                  <NotEntitled feature="Package sourcing" message={packagesError.message} />
                ) : (
                  <SectionError
                    error={packagesError}
                    onRetry={loadPackages}
                    label="Packages failed to load"
                  />
                )
              ) : !packages ? (
                <SectionLoading label="Reading this project's packages…" />
              ) : rows.length === 0 ? (
                <SectionEmpty
                  icon={PackageIcon}
                  title="No packages on this project yet"
                  hint="Create bid packages in Procurement; the Exchange sources against them."
                />
              ) : (
                <div className="space-y-2">
                  {rows.map((row) => (
                    <AssemblyRow
                      key={row.packageId}
                      row={row}
                      selected={row.packageId === packageId}
                      onSelect={selectPackage}
                    />
                  ))}
                </div>
              )}

              {/* Honest disclosure of what this list does and does not know */}
              <div className="space-y-1 pt-1">
                {!quoteCountsAvailable && (
                  <p className="text-[11px] text-muted-foreground">
                    Quote counts need the RFQ surface, which is unavailable here — package
                    states are based on status alone.
                  </p>
                )}
                {matchPreviewCapped && !matchProbeError && (
                  <p className="text-[11px] text-muted-foreground">
                    Candidate counts were pre-loaded for the first {MATCH_PREVIEW_CAP} packages
                    needing sourcing. Open a package to load the rest.
                  </p>
                )}
                {rows.length > MATCH_PREVIEW_CAP && (
                  <p className="text-[11px] text-muted-foreground">
                    RFQ handoff state was read for the first {MATCH_PREVIEW_CAP} packages. Open a
                    package to read the rest.
                  </p>
                )}
                {matchProbeError && (
                  <p className="text-[11px] text-muted-foreground">
                    {matchProbeError.kind === "not-entitled"
                      ? "Provider matching is not part of your plan, so sourcing state is unknown."
                      : matchProbeError.kind === "disabled"
                        ? "Provider matching is not enabled here, so sourcing state is unknown."
                        : "Provider matching is unavailable, so sourcing state is unknown."}
                  </p>
                )}
              </div>
            </section>

            {/* SELECTED PACKAGE */}
            <section className="space-y-3 min-w-0">
              {!packageId ? (
                <SectionEmpty
                  icon={Search}
                  title="Select a package to source it"
                  hint="Pick a package on the left to see what it requires, who can fulfil it, and who actually qualifies."
                />
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold font-heading truncate">
                        {selectedRow?.title || "Package"}
                      </h2>
                      {selectedRow?.detail && (
                        <p className="text-xs text-muted-foreground">{selectedRow.detail}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/procurement/packages/${packageId}`)}
                      title="Open this package's RFQ, quotes, leveling and award workspace"
                    >
                      <Send className="w-4 h-4 mr-1.5" />
                      RFQ workspace
                    </Button>
                  </div>

                  {sourcingLoading ? (
                    <SectionLoading label="Loading what this package needs and who can fulfil it…" />
                  ) : sourcingError ? (
                    sourcingError.kind === "not-entitled" ? (
                      <NotEntitled feature="The Exchange" message={sourcingError.message} />
                    ) : sourcingError.kind === "disabled" ? (
                      <FeatureDisabled
                        feature="The Exchange"
                        hint="Provider matching and demand profiles are built but not enabled for this environment."
                      />
                    ) : sourcingError.kind === "no-access" ? (
                      <NoAccess message={sourcingError.message} />
                    ) : (
                      <SectionError
                        error={sourcingError}
                        onRetry={loadSourcing}
                        label="Sourcing failed to load"
                      />
                    )
                  ) : sourcingData ? (
                    <>
                    {/* The whole package in one read, before the tabs that
                        let a PM act on any one part of it. */}
                    <PackageStoryPanel
                      row={selectedRow}
                      sourcing={sourcingData}
                      shortlist={shortlistData}
                      releases={releasesData}
                      requirements={storyData?.requirements}
                      requirementsTruncated={storyData?.requirementsTruncated}
                      awardProposals={storyData?.awardProposals}
                      realism={realism?.data}
                      onOpen={openWorkspace}
                    />

                    <Tabs value={tab} onValueChange={selectTab} className="space-y-3">
                      <TabsList className="bg-muted/50 flex-wrap h-auto">
                        <TabsTrigger value="providers">
                          <Users className="w-4 h-4 mr-1.5" />
                          Providers
                          {sourcingData.counts ? ` (${sourcingData.candidateCount ?? 0})` : ""}
                        </TabsTrigger>
                        <TabsTrigger value="shortlist">
                          <ListChecks className="w-4 h-4 mr-1.5" />
                          Shortlist
                          {shortlistData ? ` (${shortlistData.count ?? 0})` : ""}
                          {shortlistAwaiting > 0 && (
                            <span
                              className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500/25 px-1 text-[9px] font-bold text-amber-300"
                              title={`${shortlistAwaiting} awaiting your decision`}
                            >
                              {shortlistAwaiting}
                            </span>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="rfq">
                          <Send className="w-4 h-4 mr-1.5" />
                          RFQ handoff
                          {releasesData ? ` (${releasesData.count ?? 0})` : ""}
                          {releasesAwaitingAuthorization > 0 && (
                            <span
                              className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500/25 px-1 text-[9px] font-bold text-amber-300"
                              title={`${releasesAwaitingAuthorization} awaiting release authorization`}
                            >
                              {releasesAwaitingAuthorization}
                            </span>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="requirements">
                          <ClipboardList className="w-4 h-4 mr-1.5" />
                          What this needs
                        </TabsTrigger>
                        <TabsTrigger value="twin" data-testid="tab-twin">
                          <FlaskConical className="w-4 h-4 mr-1.5" />
                          What if we choose
                        </TabsTrigger>
                        <TabsTrigger value="memory" data-testid="tab-memory">
                          <History className="w-4 h-4 mr-1.5" />
                          What we knew then
                        </TabsTrigger>
                        <TabsTrigger value="barriers" data-testid="tab-barriers">
                          <OctagonAlert className="w-4 h-4 mr-1.5" />
                          What stands in the way
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="providers" className="space-y-3">
                        {sourcingData.matchingFailed ? (
                          <SectionError
                            error={sourcingData.matchingFailed}
                            onRetry={loadSourcing}
                            label="Provider matching failed — the package requirements below still loaded"
                          />
                        ) : (
                          <>
                            {/* Counts, stated as the backend states them */}
                            {sourcingData.counts && (
                              <div className="flex flex-wrap gap-2">
                                <Badge
                                  variant="outline"
                                  className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 font-medium"
                                >
                                  {sourcingData.counts.pass || 0} eligible
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-medium"
                                >
                                  {sourcingData.counts.unknown || 0} unverified
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="bg-red-500/20 text-red-400 border-red-500/30 font-medium"
                                >
                                  {sourcingData.counts.fail || 0} ineligible
                                </Badge>
                              </div>
                            )}

                            {emptyMeaning ? (
                              <SectionEmpty
                                icon={Users}
                                title={emptyMeaning.title}
                                hint={emptyMeaning.detail}
                              />
                            ) : (
                              <>
                                {["PASS", "UNKNOWN", "FAIL"].map((status) => {
                                  const list = grouped?.[status] || [];
                                  if (list.length === 0) return null;
                                  const heading =
                                    status === "PASS"
                                      ? "Eligible"
                                      : status === "UNKNOWN"
                                        ? "Eligibility unverified — human judgement required"
                                        : "Ineligible — fails a mandatory requirement";
                                  return (
                                    <div key={status} className="space-y-2">
                                      <h3 className="text-sm font-semibold">
                                        {heading}
                                        <span className="text-muted-foreground font-normal">
                                          {" "}
                                          ({list.length})
                                        </span>
                                      </h3>
                                      {list.map((candidate) => (
                                        <ProviderMatchCard
                                          key={candidate.listingId}
                                          candidate={candidate}
                                          shortlistState={shortlistedListingIds[candidate.listingId]}
                                          onOpenPassport={(orgId) =>
                                            navigate(`/exchange/providers/${orgId}`)
                                          }
                                        />
                                      ))}
                                    </div>
                                  );
                                })}
                              </>
                            )}

                            {(sourcingData.missingInformation || []).length > 0 && (
                              <Card className="border-amber-500/30 bg-amber-500/5">
                                <CardContent className="py-3 px-4 space-y-0.5">
                                  <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    What OMNΛS could not establish
                                  </p>
                                  {sourcingData.missingInformation.map((line, i) => (
                                    <p key={i} className="text-xs text-muted-foreground">
                                      {line}
                                    </p>
                                  ))}
                                </CardContent>
                              </Card>
                            )}

                            {sourcingData.truncated && (
                              <p className="text-[11px] text-muted-foreground">
                                More listings existed than were scanned — this comparison reads a
                                bounded set.
                              </p>
                            )}
                          </>
                        )}
                      </TabsContent>

                      <TabsContent value="shortlist">
                        {shortlistError ? (
                          <ClassifiedSectionState
                            classified={shortlistError}
                            feature="The shortlist"
                            onRetry={loadShortlist}
                          />
                        ) : !shortlistData ? (
                          <SectionLoading label="Reading this package's shortlist…" />
                        ) : (
                          <ShortlistPanel
                            shortlist={shortlistData}
                            candidatesByListing={candidatesByListing}
                            matchSetLoaded={Boolean(sourcingData?.candidates)}
                            canPropose={canPropose}
                            canDecide={canDecide}
                            capabilitiesEstablished={capabilitiesEstablished}
                            canPrepare={canPrepareRfq}
                            onPrepare={prepareRfq}
                            preparing={preparingEntries}
                            releaseStateByEntry={releaseStateByEntry}
                            proposing={proposing}
                            deciding={decidingEntry}
                            onPropose={proposeShortlist}
                            onDecide={decideShortlistEntry}
                            onOpenPassport={(orgId) => navigate(`/exchange/providers/${orgId}`)}
                          />
                        )}
                      </TabsContent>

                      <TabsContent value="rfq">
                        {releasesError ? (
                          <ClassifiedSectionState
                            classified={releasesError}
                            feature="The RFQ handoff"
                            onRetry={loadReleases}
                          />
                        ) : !releasesData ? (
                          <SectionLoading label="Reading this package's RFQ handoff records…" />
                        ) : (
                          <RfqHandoffPanel
                            releases={releasesData}
                            candidatesByOrganization={candidatesByOrganization}
                            canAuthorize={canAuthorizeRelease}
                            capabilitiesEstablished={capabilitiesEstablished}
                            authorizing={authorizing}
                            onAuthorize={authorizeRelease}
                            onOpenPackage={() =>
                              navigate(`/procurement/packages/${packageId}`)
                            }
                          />
                        )}
                      </TabsContent>

                      <TabsContent value="requirements">
                        <DemandProfilePanel profile={sourcingData.demandProfile} />
                      </TabsContent>

                      {/* The runtime's answer for THIS package. Narrowed from
                          the project-wide response by bid package id; a miss
                          renders the absence rather than another package's
                          barriers. */}
                      <TabsContent value="twin">
                        <DecisionTwinPanel
                          quotes={twin?.packageId === packageId ? twin.quotes : []}
                          scenarios={
                            scenarios.packageId === packageId ? scenarios.byQuote : {}
                          }
                          errors={scenarios.packageId === packageId ? scenarios.errors : {}}
                          loadingQuoteId={runningQuoteId}
                          quotesLoading={twinLoading}
                          quotesError={twinError}
                          onRun={runScenario}
                        />
                      </TabsContent>

                      <TabsContent value="memory">
                        <DecisionMemoryPanel
                          memory={memory?.projectId === projectId ? memory.presented : null}
                          contractVersion={
                            memory?.projectId === projectId ? memory.contractVersion : null
                          }
                          loading={memoryLoading}
                          error={memoryError}
                        />
                      </TabsContent>

                      <TabsContent value="barriers">
                        <ExecutionBarrierMap
                          barrier={
                            barrier?.projectId === projectId
                              ? findBarrierPackage(barrier.presented, packageId)
                              : null
                          }
                          contractVersion={
                            barrier?.projectId === projectId ? barrier.contractVersion : null
                          }
                          loading={barrierLoading}
                          error={barrierError}
                          onRetry={loadBarrier}
                        />
                      </TabsContent>
                    </Tabs>
                    </>
                  ) : null}
                </>
              )}
            </section>
          </div>
        </>
      )}

      <RfqBlockerDialog
        blocker={rfqBlocker}
        onClose={() => setRfqBlocker(null)}
        onOpenProviders={() => navigate("/exchange/providers")}
      />
    </div>
  );
};

export default BuildMyProjectPage;
